import { Link, useLocation } from 'react-router-dom';
import css from './NavigationTabs.module.css';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavigationTab } from '@shared/Interfaces/Navigation/NavigationTab';
import { CustomProjectStep2ElementsIdsEnum } from '@modules/Steps/Shared/Enums/CustomProjectStep2ElementsIdsEnum';
import classNames from 'classnames';

interface NavigationTabsProps {
    tabs: NavigationTab[];
    className?: string;
}

export const NavigationTabs: React.FC<NavigationTabsProps> = ({
    tabs,
    className,
}) => {
    const location = useLocation();
    const activeIndex = tabs.findIndex(tab => location.pathname === tab.path);
    const containerRef = useRef<HTMLDivElement>(null);
    const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
    const [hasMounted, setHasMounted] = useState(false);

    useLayoutEffect(() => {
        if (!containerRef.current || activeIndex < 0) {
            setSliderStyle({ left: 0, width: 0 });
            return;
        }

        const links = containerRef.current.querySelectorAll<HTMLAnchorElement>(
            `.${css.navTab}`
        );
        const activeLink = links[activeIndex];

        if (!activeLink) {
            return;
        }

        const updateSliderPosition = (target: HTMLAnchorElement) => {
            const { offsetLeft, offsetWidth } = target;
            setSliderStyle({ left: offsetLeft, width: offsetWidth });
        };

        updateSliderPosition(activeLink);

        if (!hasMounted) {
            setHasMounted(true);
        }

        const resizeObserver = new ResizeObserver(entries => {
            if (entries[0]) {
                const target = entries[0].target as HTMLAnchorElement;
                updateSliderPosition(target);
            }
        });

        resizeObserver.observe(activeLink);

        return () => {
            resizeObserver.disconnect();
        };
    }, [activeIndex, tabs]);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current && activeIndex >= 0) {
                const links =
                    containerRef.current.querySelectorAll<HTMLAnchorElement>(
                        `.${css.navTab}`
                    );
                const activeLink = links[activeIndex];
                if (activeLink) {
                    const { offsetLeft, offsetWidth } = activeLink;
                    setSliderStyle({ left: offsetLeft, width: offsetWidth });
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [activeIndex]);

    return (
        <div
            className={`${css.container} ${className}`}
            ref={containerRef}
            id={CustomProjectStep2ElementsIdsEnum.STEPS_NAVIGATE_HEADER}
        >
            {tabs.map((tab, index) => {
                const isActive = index === activeIndex;
                return (
                    <Link
                        className={`${css.navTab} ${isActive ? css.active : ''} ${tab.disabled ? css.disabled : ''}`}
                        key={index}
                        to={tab.path}
                    >
                        <div
                            className={`${css.tabLink} ${tab.linkClassName ? tab.linkClassName : ''}
                                 ${
                                     isActive
                                         ? tab.activeLinkClassName
                                             ? tab.activeLinkClassName
                                             : css.active
                                         : ''
                                 }`}
                        >
                            <div className={css.badgeAndIcon}>
                                {tab.badge && (
                                    <span className={css.tabBadge}>
                                        {tab.badge}
                                    </span>
                                )}
                                <span>{tab.icon && tab.icon}</span>
                            </div>
                            <div className={css.label}>{tab.label}</div>
                        </div>
                    </Link>
                );
            })}
            <div
                className={classNames(css.tabsSlider, {
                    [css.animated]: hasMounted,
                })}
                style={{
                    left: sliderStyle.left,
                    width: sliderStyle.width,
                }}
            />
        </div>
    );
};
